// Plan change models for the SaaS billing prototype.
// See plans/saas-billing/04-change-plan-and-seats.md for canonical types.
//
// These types power the change-plan modal flow (steps 1-5). They are returned
// from IBillingMockService.previewPlanChange / commitPlanChange and consumed
// by the change-plan modal components.

import { BillingPeriod, CurrencyCode, TierCode } from './subscription.model';

/**
 * Input passed to previewPlanChange / commitPlanChange. The trio
 * (targetTier, targetSeats, targetBillingPeriod) fully determines the new
 * subscription shape.
 */
export interface PlanChangeInput {
  targetTier: TierCode;
  targetSeats: number;
  targetBillingPeriod: BillingPeriod;
}

/**
 * Single line in the today's-charge breakdown (proration). amountMinor is
 * positive for charges, negative for credits.
 */
export interface ProrationLine {
  description: string;
  periodStart: string; // ISO 8601
  periodEnd: string;   // ISO 8601
  amountMinor: number; // negative = credit, positive = charge
}

/**
 * Non-blocking warning shown above the proration breakdown in step 3.
 * Discriminated by `code` so each variant can carry the data the UI needs to
 * format the message.
 */
export type PlanChangeWarning =
  | { code: 'USAGE_EXCEEDS_NEW_ALLOCATION'; usedCredits: number; newAllocation: number }
  | { code: 'SEATS_BELOW_ACTIVE_USERS'; activeUsers: number; newSeats: number }
  | { code: 'SWITCHING_TO_ANNUAL'; firstAnnualChargeMinor: number; nextRenewalAt: string }
  | { code: 'SWITCHING_TO_MONTHLY'; accountCreditAppliedMinor: number };

/**
 * Error codes the mock returns on preview / commit. Surfaced inline to the
 * user — the UI maps these to copy + retry affordances.
 */
export type PlanChangeError =
  | 'PREVIEW_STALE'    // commit was passed a token that no longer matches the input hash
  | 'NO_OP'            // selection equals current subscription
  | 'CARD_DECLINED';   // simulated card decline on commit

/**
 * Full preview returned by previewPlanChange. All money values in minor units
 * (matches the *Minor convention from #01).
 *
 * `accountCreditAppliedMinor` semantics: when the proration math results in a
 * net credit (subtotal - discount < 0), totalMinor is clamped to 0 and the
 * absolute value of the net credit is placed here. UI renders "$0 today +
 * $X applied as account credit".
 */
export interface PlanChangePreview {
  previewToken: string;
  currentSubtotalMinor: number;
  newSubtotalMinor: number;
  prorationLines: ProrationLine[];
  subtotalMinor: number;           // sum of prorationLines
  discountMinor: number;           // from active coupon, applied to subtotal
  totalMinor: number;              // max(0, subtotal - discount); never negative
  accountCreditAppliedMinor: number;
  effectiveDate: string;           // ISO 8601 - when the change takes effect
  // immediate: the change applies now (charge or credit per Solo's rules);
  // renewal: it starts at the next renewal and nothing is charged today (ADO 19427).
  changeTiming?: 'immediate' | 'renewal';
  nextRegularChargeAt: string;     // ISO 8601 - when the next regular charge lands
  nextRegularChargeMinor: number;  // amount of next regular charge
  currency: CurrencyCode;
  warnings: PlanChangeWarning[];
  resolvedInput: PlanChangeInput;
}
